import {inject, Injectable, InjectionToken} from '@angular/core';
import {BarcodeScanner} from '@capacitor-mlkit/barcode-scanning';
import {Capacitor} from '@capacitor/core';
import {NativeBarcodeScanner} from './native-barcode-scanner';
import {CodeScan, toCodeScan, universalFormats} from './code-scan';

import {WebCodeScanSession, WebCodeDetector, webProductFormats} from './web-code-scan-session';

type DetectorClass = {new(options: {formats: string[]}): WebCodeDetector; getSupportedFormats(): Promise<string[]>};
const nativeScanAborted = new Error('native-scan-aborted');

export const NATIVE_BARCODE_SCANNER = new InjectionToken<NativeBarcodeScanner>('NativeBarcodeScanner', {
  providedIn: 'root', factory: () => ({
    scan: options => BarcodeScanner.scan(options),
    requestPermissions: () => BarcodeScanner.requestPermissions(),
    isGoogleBarcodeScannerModuleAvailable: () => BarcodeScanner.isGoogleBarcodeScannerModuleAvailable(),
    installGoogleBarcodeScannerModule: () => BarcodeScanner.installGoogleBarcodeScannerModule(),
  }),
});

@Injectable({providedIn: 'root'})
export class UniversalScannerService {
  private readonly nativeScanner = inject(NATIVE_BARCODE_SCANNER);
  readonly isNative = Capacitor.isNativePlatform();
  private nativePending?: Promise<CodeScan | null>;
  private moduleInstallPending?: Promise<void>;

  async scanNative(signal?: AbortSignal): Promise<CodeScan | null> {
    if (signal?.aborted) return null;
    if (this.nativePending) {
      if (!signal) return null;
      try {
        await this.awaitWithAbort(this.nativePending.catch(() => null), signal);
        return this.scanNative(signal);
      } catch (error) {
        if (error === nativeScanAborted) return null;
        throw error;
      }
    }
    const pending = this.performNativeScan(signal);
    this.nativePending = pending;
    try { return await pending; }
    finally { if (this.nativePending === pending) this.nativePending = undefined; }
  }

  private async performNativeScan(signal?: AbortSignal): Promise<CodeScan | null> {
    try {
      await this.prepareNative(signal);
      if (signal?.aborted) return null;
      const result = await this.nativeScanner.scan({formats: universalFormats, autoZoom: true});
      const barcode = result.barcodes[0];
      if (!barcode) return null;
      const scan = toCodeScan(barcode.rawValue ?? '', barcode.format);
      if (!scan) throw new Error('unsupported-code');
      return scan;
    } catch (error) {
      if (error === nativeScanAborted) return null;
      // Capacitor may reject with a plain {message} object rather than an Error.
      const message = typeof error === 'string' ? error
        : error && typeof error === 'object' && 'message' in error ? error.message : '';
      if (typeof message === 'string' && /^scan cancel(?:led|ed)\.?$/i.test(message.trim())) return null;
      throw error;
    }
  }

  startWeb(video: HTMLVideoElement, detected: (scan: CodeScan | null) => void,
    barcodeUnavailable: () => void): WebCodeScanSession {
    return new WebCodeScanSession(video, async decoded => {
      const {default: QrScanner} = await import('qr-scanner');
      return new QrScanner(video, result => decoded(result.data), {
        preferredCamera: 'environment', maxScansPerSecond: 8, returnDetailedScanResult: true,
        onDecodeError: () => {},
      });
    }, () => this.createWebDetector(), detected, barcodeUnavailable);
  }
  private async createWebDetector(): Promise<WebCodeDetector | null> {
    try {
      const Detector = (window as unknown as {BarcodeDetector?: DetectorClass}).BarcodeDetector;
      if (!Detector) return null;
      const supported = await Detector.getSupportedFormats();
      const formats = Object.keys(webProductFormats).filter(format => supported.includes(format));
      return formats.length === Object.keys(webProductFormats).length ? new Detector({formats}) : null;
    } catch { return null; }
  }

  private async prepareNative(signal?: AbortSignal): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
      if ((await this.awaitWithAbort(this.nativeScanner.requestPermissions(), signal)).camera !== 'granted') {
        throw new Error('camera-denied');
      }
      return;
    }
    if ((await this.awaitWithAbort(this.nativeScanner.isGoogleBarcodeScannerModuleAvailable(), signal)).available) return;
    if (signal?.aborted) throw nativeScanAborted;
    await this.awaitWithAbort(this.installNativeModule(), signal);
    const deadline = Date.now() + 15000;
    while (Date.now() < deadline) {
      if ((await this.awaitWithAbort(this.nativeScanner.isGoogleBarcodeScannerModuleAvailable(), signal)).available) return;
      await this.awaitWithAbort(new Promise<void>(resolve => setTimeout(resolve, 500)), signal);
    }
    throw new Error('scanner-unavailable');
  }

  private installNativeModule(): Promise<void> {
    if (!this.moduleInstallPending) {
      const pending = this.nativeScanner.installGoogleBarcodeScannerModule();
      this.moduleInstallPending = pending;
      void pending.then(
        () => { if (this.moduleInstallPending === pending) this.moduleInstallPending = undefined; },
        () => { if (this.moduleInstallPending === pending) this.moduleInstallPending = undefined; },
      );
    }
    return this.moduleInstallPending;
  }

  private async awaitWithAbort<T>(operation: Promise<T>, signal?: AbortSignal): Promise<T> {
    if (!signal) return operation;
    if (signal.aborted) throw nativeScanAborted;
    return new Promise<T>((resolve, reject) => {
      const abort = () => reject(nativeScanAborted);
      signal.addEventListener('abort', abort, {once: true});
      operation.then(
        value => { signal.removeEventListener('abort', abort); resolve(value); },
        error => { signal.removeEventListener('abort', abort); reject(error); },
      );
    });
  }
}
